import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell } from 'recharts';
import IndexChipRows from './IndexChipRows';
import { Skel } from './Skeleton';
import ProcessingStatus, { dimClass } from './ProcessingStatus';
import SegmentControl from './SegmentControl';
import { FILTER_LABEL, FILTER_ROW, FilterSelect, SelectorToggle, PeriodControl, EXECUTION_OPTIONS } from './FilterControls';
import { apiService } from '../services/api';
import { rollingWindows, windowStats, HORIZON_OPTIONS } from '../utils/horizons';
import { isCanceled } from '../utils/latestRequest';

// Strategy > Allocation. Three ideas, kept strictly separate:
//   1. WHICH indexes play, and long vs long+short — on the selector chips,
//      exactly like Signal Analysis (L+S is a property of the index).
//   2. ALLOCATION — pure percentages, one card per strategy type; only the
//      Custom card is editable. Every card recomputes over YOUR selection.
//   3. One table decides: per-year winners, years-won tally, CAGR.
//
// The universe is DYNAMIC (Apal 2026-09-18: "this should be all dynamic").
// Nothing about which indexes exist — or which offer L+S — lives in this
// file: /analytics/universe/?scope=allocation serves the user's permitted
// instruments with each symbol's L+S verdict (data capability ∩ instrument
// tradability ∩ the user's shorts grant). A new instrument in the engine
// config appears here with zero frontend changes.
let UNIVERSE = { regions: [], names: [], cryptoSet: new Set(), shortable: new Set(), labels: {} };
export function setUniverse(regions) {
  UNIVERSE = {
    regions,
    names: regions.flatMap(r => r.symbols.map(s => s.name)),
    cryptoSet: new Set(regions.filter(r => r.code === 'CRYPTO').flatMap(r => r.symbols.map(s => s.name))),
    shortable: new Set(regions.flatMap(r => r.symbols.filter(s => s.shorts).map(s => s.name))),
    labels: Object.fromEntries(regions.flatMap(r => r.symbols.map(s => [s.name, s.label || s.name]))),
  };
  return UNIVERSE;
}
// Display-only fallback when a strategy needs weights before the server
// answers — the REAL data-driven weights always come from the engine.
const DATA_DRIVEN_BASE = { NIFTY50: 5, NIFTYBANK: 15, NIFTYIT: 5, NIFTYMIDCAP100: 5, NIFTYSMALLCAP100: 5, SNP: 5, IXIC: 20, RUT: 15, BTC: 15, ETH: 10 };
const FALLBACK_YEARS = [2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025, 2026];
const NICE = { NIFTY50: 'Nifty 50', NIFTYBANK: 'Bank', SENSEX: 'Sensex', NIFTYIT: 'IT', NIFTYMIDCAP100: 'Midcap', NIFTYSMALLCAP100: 'Smallcap', SNP: 'S&P 500', IXIC: 'Nasdaq', DJI: 'Dow', RUT: 'Russell 2k', BTC: 'BTC', ETH: 'ETH' };
// Why each index carries its data-driven weight — shown read-only from the
// Data-driven row so the recommendation stays inspectable without being editable.
const WEIGHT_REASONS = {
  NIFTY50: 'Deepest futures liquidity in India; 69.9% win rate at 16.2% book volatility — the India anchor.',
  NIFTYBANK: 'Liquid futures but 20.9% book volatility, the highest in India — trimmed once the US sleeves joined.',
  SENSEX: '0.99 correlation with Nifty 50 and a weaker signal book — token weight; its share works harder elsewhere.',
  NIFTYIT: 'Single-sector concentration; shorts only via basket — kept small.',
  NIFTYMIDCAP100: 'Has its own futures (MIDCPNIFTY); 65.1% win rate — holds its mandate floor in the 10-sleeve book.',
  NIFTYSMALLCAP100: 'The one index with NO futures — depth comes from ETF create/redeem against the 250-stock basket (~₹2,000 Cr/day executable); capped at 5% as a thin instrument.',
  DJI: 'The calmest US book: 12.2% downside risk vs Nasdaq 16.6 — Risk-balanced sizes it UP for exactly that. Its index is a 0.96 S&P clone, but our BOOKS are only 0.59 correlated (signals fire on different days), so it genuinely diversifies.',
  RUT: 'Distinct small-cap book (0.87 index corr with the S&P, real RTY futures depth) — 14.7% downside risk sits between the S&P and Nasdaq.',
  SNP: 'Deepest equity contract on earth (CME E-mini); 73.9% win rate at 18.2% book volatility, and 1996-2005 history no other sleeve has.',
  IXIC: 'The highest-earning US book (40%/yr mean) AND the most volatile (16.6% downside risk) — Best-returns sizes it up; Risk-balanced deliberately sizes it down. Which you see depends on the Method you picked.',
  BTC: 'The return engine: 60.3% win rate at 31.4% book volatility — band-capped at 15% for drawdown control.',
  ETH: '72.9% win rate but 45.9% book volatility — highest octane in the book; sized below BTC by mandate.',
};

// Sizing rules for the Data-driven strategy — weights come from a stated
// equation computed server-side on our books; the client only picks the rule.


// Stable sleeve colors — donut, legend and any future charts stay in sync.
// Unknown (future) symbols get a deterministic color from the spare palette.
const SLEEVE_COLORS = {
  NIFTY50: '#14b8a6', NIFTYBANK: '#0ea5e9', SENSEX: '#64748b', NIFTYIT: '#8b5cf6',
  NIFTYMIDCAP100: '#f59e0b', NIFTYSMALLCAP100: '#f43f5e', SNP: '#22c55e', IXIC: '#3b82f6',
  BTC: '#f97316', ETH: '#6366f1', DJI: '#a855f7', RUT: '#84cc16',
};
const SPARE_COLORS = ['#e879f9', '#2dd4bf', '#facc15', '#fb7185', '#a3e635', '#38bdf8'];
export const sleeveColor = (sym) => SLEEVE_COLORS[sym]
  || SPARE_COLORS[[...String(sym)].reduce((a, c) => a + c.charCodeAt(0), 0) % SPARE_COLORS.length];

const SCENARIO_LABELS = { data_driven: 'Data-driven ★', equal: 'Equal allocation', custom: 'Custom' };
// Danger / warning / normal (Apal 2026-09-25: "lets put 10% as the bar, so
// anytime we make money less than 10% we show yellow & if negative then
// that gotta be REDish... It's like danger, warning & normal").
const SATISFACTORY_PCT = 10;

// A year the book could not trade is NOT a thin year — before its first
// index existed the book sits in cash and prints 0%, and washing that amber
// is a verdict on nothing (Apal: "the data wasn't even available for
// nifty50/bank"). No live index that year => no band, no claim.
export const bookWasLive = (weights, inceptions, y) => {
  const inc = inceptions || {};
  const held = Object.keys(weights || {}).filter(sym => weights[sym] > 0);
  if (!held.length) return false;
  return held.some(sym => inc[sym] == null || inc[sym] <= y);
};

const yearBand = (v, live) => {
  if (v == null || !live) return null;
  if (v < 0) return 'danger';
  return v < SATISFACTORY_PCT ? 'warning' : null;
};
// Score tone — the same thresholds the server labels with.
// Score = share of live years that cleared the satisfactory bar.
const scoreTone = (t) => (t >= 80 ? 'text-emerald-400' : t >= 65 ? 'text-emerald-300'
  : t >= 50 ? 'text-amber-300' : 'text-red-300');

// Apples-to-apples honesty (Apal 2026-09-24): a strategy whose sleeves exceed
// their EXECUTABLE caps at this amount (what the market absorbs in one fill,
// SEBI position ceiling, concentration) is flagged — Custom stays editable and
// evaluable, but a 70% Nifty book at ₹1K Cr holds 8x what Nifty futures
// absorb and must never LOOK equivalent to one that can actually trade.
// House crypto policy (Apal 2026-09-25: 'we need to have a crypto cap tbh,
// in everything... 30% allocation in crypto is too much'): BTC+ETH share of
// any strategy above the dial is flagged and loses crown eligibility.
export function cryptoCapExcess(weights, instruments, capPct) {
  const cryptoSyms = new Set((instruments || []).filter(i => i.market === 'crypto').map(i => i.symbol));
  if (cryptoSyms.size === 0) { cryptoSyms.add('BTC'); cryptoSyms.add('ETH'); }
  const pct = Object.entries(weights || {}).reduce((a, [sym, v]) => a + (cryptoSyms.has(sym) ? v : 0), 0);
  return pct > capPct + 1e-9 ? { pct, cap: capPct } : null;
}

// Apples-to-apples THROUGH TIME (Apal 2026-09-25 full-window attack): a
// non-searched strategy renormalizes to 100% deployed in eras where some of
// its sleeves aren't live yet — before 2006 a 20/20 S&P/Nasdaq book becomes
// 50/50, breaching the concentration walls the searched era books honor by
// holding cash. Those era numbers aren't comparable: flag them, no crown.
export function eraBreaches(weights, instruments, inceptions, fromYear, toYear) {
  const inst = Object.fromEntries((instruments || []).map(i => [i.symbol, i]));
  const inc = inceptions || {};
  const years = [...new Set(Object.values(inc))].sort();
  const out = [];
  for (const era of years) {
    const live = Object.entries(weights || {}).filter(([sym, v]) => v > 0 && (inc[sym] ?? -Infinity) <= era);
    const total = live.reduce((a, [, v]) => a + v, 0);
    const next = years.find(y => y > era) || Infinity;
    if (!total || total >= 99.5) continue;               // fully live — no renorm
    if (next <= (fromYear ?? -Infinity) || era > (toYear ?? Infinity)) continue; // era not displayed
    for (const [sym, v] of live) {
      const cap = inst[sym]?.concentration_cap_pct;
      const renorm = (v * 100) / total;
      if (cap != null && renorm > cap + 0.5) {
        const span = next === Infinity ? `${era}+` : `${era}–${next - 1}`;
        out.push(`${sym} ${Math.round(renorm)}% in the ${span} era vs its ${cap}% cap`);
      }
    }
  }
  return out;
}

// What the market absorbs in one fill, as % of this amount — the engine's
// served max_position_cr (depth rule), the SAME number the sizing uses.
export function depthPct(ins, fundCr) {
  return ins.max_position_cr != null ? 100 * ins.max_position_cr / Math.max(fundCr, 1e-9) : Infinity;
}

export function strategyBreaches(weights, instruments, fundCr) {
  const out = [];
  (instruments || []).forEach(ins => {
    const w = weights?.[ins.symbol] || 0;
    if (w <= 0) return;
    const liq = depthPct(ins, fundCr);
    const pos = ins.pos_limit_cr ? 100 * ins.pos_limit_cr / Math.max(fundCr, 1e-9) : Infinity;
    // Execution feasibility ONLY (depth + SEBI ceiling) — Custom is allowed
    // to express a concentrated VIEW past the policy caps; it is never
    // allowed to look executable when the position cannot exist.
    const eff = Math.min(100, liq, pos);
    if (w > eff + 0.5) {
      const posCr = Math.round(w * fundCr / 100);
      const why = pos < liq
        ? `position ₹${posCr.toLocaleString()} Cr vs SEBI ceiling ₹${ins.pos_limit_cr.toLocaleString()} Cr`
        : `position ₹${posCr.toLocaleString()} Cr vs ₹${Math.round(ins.max_position_cr || 0).toLocaleString()} Cr the market absorbs in one fill`;
      out.push(`${ins.symbol} ${w}% > ${eff.toFixed(1)}% executable — ${why}`);
    }
  });
  return out;
}

// Snap a raw weight map to the 5%-grid, exactly 100 total (largest remainder)
export function snapTo5(raw) {
  const syms = Object.keys(raw).filter(k => raw[k] > 0);
  if (!syms.length) return {};
  const total = syms.reduce((a, k) => a + raw[k], 0);
  const exact = syms.map(k => ({ k, v: (raw[k] / total) * 100 }));
  const snapped = exact.map(({ k, v }) => ({ k, v: Math.floor(v / 5) * 5, frac: v % 5 }));
  let drift = 100 - snapped.reduce((a, e) => a + e.v, 0);
  snapped.sort((a, b) => b.frac - a.frac);
  for (let i = 0; drift > 0; i = (i + 1) % snapped.length) { snapped[i].v += 5; drift -= 5; }
  return Object.fromEntries(snapped.filter(e => e.v > 0).map(e => [e.k, e.v]));
}

// Auto weightage per strategy type, computed over the CURRENT selection
export function autoWeights(label, selected) {
  if (label === 'data_driven') {
    return snapTo5(Object.fromEntries(selected.map(sym => [sym, DATA_DRIVEN_BASE[sym] || 5])));
  }
  if (label === 'equal') {
    // brutally equal — 12.5% each for 8, 16.67% for 6; no grid snapping
    if (!selected.length) return null;
    const each = Math.round((100 / selected.length) * 100) / 100;
    const w = Object.fromEntries(selected.map(sym => [sym, each]));
    const drift = Math.round((100 - each * selected.length) * 100) / 100;
    w[selected[0]] = Math.round((w[selected[0]] + drift) * 100) / 100;
    return w;
  }
  if (label === 'custom') {
    // Default seed: the "client loves Nifty 50, no crypto" persona — 70% N50,
    // rest across India only. Adding crypto here is the USER's explicit
    // choice via Edit; the default keeps the diversification story honest.
    const nonCrypto = selected.filter(sym => !UNIVERSE.cryptoSet.has(sym));
    const base = nonCrypto.length ? nonCrypto : selected;
    if (base.includes('NIFTY50') && base.length >= 2) {
      const rest = base.filter(sym => sym !== 'NIFTY50');
      return { NIFTY50: 70, ...snapReduceTo(Object.fromEntries(rest.map(sym => [sym, 1])), 30) };
    }
    return snapTo5(Object.fromEntries(base.map(sym => [sym, 1])));
  }
  return null;
}

export const STRATEGY_ORDER = ['data_driven', 'equal', 'custom'];

// One book as a donut + two-column legend — the header's look, reused for the
// earlier books in the year rows (Apal 2026-10-29: "this is super clean, you
// need to maintain this below when fewer indexes are available").
export function BookDonut({ weights, minHeight, testId }) {
  const entries = Object.entries(weights || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const deployed = entries.reduce((a, [, v]) => a + v, 0);
  const cash = Math.round(100 - deployed);
  const rows = cash >= 1 ? [...entries, ['CASH', cash]] : entries;
  const pie = rows.map(([sym, v]) => ({ name: sym === 'CASH' ? 'Cash' : (NICE[sym] || sym), value: v, sym }));
  return (
    <div className="flex items-start gap-3 mt-2" style={minHeight ? { minHeight } : undefined} data-testid={testId}>
      <PieChart width={96} height={96} className="shrink-0">
        <Pie data={pie} dataKey="value" nameKey="name" cx="50%" cy="50%"
          innerRadius={28} outerRadius={46} strokeWidth={0} isAnimationActive={false}>
          {pie.map(en => <Cell key={en.sym} fill={en.sym === 'CASH' ? '#334155' : sleeveColor(en.sym)} />)}
        </Pie>
      </PieChart>
      <div className="flex-1 min-w-0 grid grid-cols-2 gap-x-3 gap-y-1 font-normal content-start">
        {rows.map(([sym, v]) => (
          <div key={sym} className="flex items-center gap-1 text-xs leading-5 min-w-0"
            title={sym === 'CASH' ? 'Market depth caps deployment at this amount — the rest sits in treasuries at 6.5%' : `${NICE[sym] || sym} ${v}%`}>
            <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: sym === 'CASH' ? '#334155' : sleeveColor(sym) }} />
            <span className={`truncate ${sym === 'CASH' ? 'text-dark-500 italic' : 'text-dark-400'}`}>{sym === 'CASH' ? 'Cash' : (NICE[sym] || sym)}</span>
            <span className="ml-auto pl-0.5 text-dark-100 font-semibold tabular-nums">{v}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// The book a strategy actually held, stretch by stretch, over the displayed
// years (Apal 2026-10-29: "look at compare index page how we show different
// indexes, depending on availability"). eras[k].mixes[ri] is the book used
// BEFORE eras[k].year; the last stretch runs on today's book.
export function bookStretches(weights, ri, eras, years) {
  if (!years || !years.length) return [];
  const y0 = years[0], y1 = years[years.length - 1];
  const out = [];
  let start = y0;
  const sorted = [...(eras || [])].sort((a, b) => a.year - b.year);
  sorted.forEach(e => {
    if (e.year <= y0 || e.year > y1) return;
    const mix = (e.mixes || [])[ri];
    if (mix) out.push({ from: start, to: e.year - 1, mix });
    start = e.year;
  });
  // the last stretch runs on today's book only if no index joins later; a
  // window ending before crypto existed ran on the next era's earlier book
  const next = sorted.find(e => e.year > y1);
  out.push({ from: start, to: y1, mix: (next && (next.mixes || [])[ri]) || weights || {} });
  return out;
}
// The largest amount the page offers (backend MAX_AMOUNT_CR).
export const MAX_AMOUNT_CR = 1000;

const sameWeights = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Locked reference rows: the data-driven recommendation must stay exactly what the
// data produced, and "equal" edited is no longer equal. Only these two take overrides.
export const EDITABLE_STRATEGIES = new Set(['custom']);

export function effectiveSets(selected, overrides) {
  const sets = [];
  for (const label of STRATEGY_ORDER) {
    const ov = EDITABLE_STRATEGIES.has(label) ? overrides[label] : null;
    const w = ov && Math.abs(Object.values(ov).reduce((x, y) => x + y, 0) - 100) < 0.01
      ? Object.fromEntries(Object.entries(ov).filter(([k, v]) => v > 0 && selected.includes(k)))
      : autoWeights(label, selected);
    if (!w || !Object.keys(w).length) continue;
    if (Math.abs(Object.values(w).reduce((x, y) => x + y, 0) - 100) > 0.01) continue;
    // A custom mix identical to an existing strategy adds noise, not signal
    if (label === 'custom' && sets.some(st => sameWeights(st.weights, w))) continue;
    sets.push({ label, weights: w, edited: !!ov });
  }
  return sets;
}

function snapReduceTo(raw, total) {
  const syms = Object.keys(raw).filter(k => raw[k] > 0);
  if (!syms.length) return {};
  const sum = syms.reduce((a, k) => a + raw[k], 0);
  const snapped = syms.map(k => {
    const v = (raw[k] / sum) * total;
    return { k, v: Math.floor(v / 5) * 5, frac: v % 5 };
  });
  let drift = total - snapped.reduce((a, e) => a + e.v, 0);
  snapped.sort((a, b) => b.frac - a.frac);
  for (let i = 0; drift > 0 && snapped.length; i = (i + 1) % snapped.length) { snapped[i].v += 5; drift -= 5; }
  return Object.fromEntries(snapped.filter(e => e.v > 0).map(e => [e.k, e.v]));
}

const load = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return v ?? fallback;
  } catch { return fallback; }
};



// The leaderboard still loading: each strategy as its column — name, donut
// with its weights, CAGR and years won, the score box — then the years down
// (Apal 2026-11-03: "appropriate skeleton … exactly what's gonna load").
const AllocationTableSkeleton = () => (
  <div className="bg-dark-800 border border-dark-700 rounded-lg overflow-hidden" data-testid="allocation-table-skeleton">
    <div className="grid grid-cols-[4rem_repeat(3,minmax(0,1fr))]">
      <div />
      {[0, 1, 2].map(i => (
        <div key={i} className="border-l border-dark-700 px-4 py-4">
          <div className="h-7 flex items-center mb-3"><Skel className="h-5 w-40" /></div>
          <div className="flex items-center gap-4 mb-4">
            <Skel className="h-24 w-24 shrink-0" rounded="rounded-full" />
            <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-2">{Array.from({ length: 8 }, (_, k) => <Skel key={k} className="h-2.5 w-full block" />)}</div>
          </div>
          <div className="flex items-end gap-6 mb-3">
            <div><Skel className="h-8 w-24 block" /><div className="mt-1.5"><Skel className="h-2.5 w-20" /></div></div>
            <div><Skel className="h-8 w-16 block" /><div className="mt-1.5"><Skel className="h-2.5 w-16" /></div></div>
          </div>
          <Skel className="h-11 w-full block" rounded="rounded-lg" />
        </div>
      ))}
    </div>
    <table className="w-full table-fixed text-sm border-t border-dark-700">
      <colgroup><col className="w-16" /><col /><col /><col /></colgroup>
      <thead><tr className="text-[11px] uppercase tracking-widest text-dark-500">
        <th />
        {[0, 1, 2].map(i => (
          <th key={i} className="text-left font-semibold py-2.5 px-4 border-l border-dark-700">
            <span className="inline-block w-1/2">Strategy</span><span>Buy &amp; hold</span>
          </th>
        ))}
      </tr></thead>
      <tbody>
        {Array.from({ length: 8 }, (_, r) => (
          <tr key={r} className="border-b border-dark-700/40">
            <td className="py-2.5 px-3"><Skel className="h-3 w-9" /></td>
            {[0, 1, 2].map(i => (
              <td key={i} className="py-2.5 px-4 border-l border-dark-700">
                <div className="flex"><span className="w-1/2"><Skel className="h-3.5 w-16" /></span><Skel className="h-3 w-14" /></div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AllocationLab({ reloadKey }) {
  // The universe arrives from the server (permission-aware); until then the
  // selector renders a skeleton and no evaluation fires. `selected` starts
  // as the STORED list and reconciles against the served names on arrival.
  const [universe, setUniverseState] = useState(null); // regions[] | null
  const [universeError, setUniverseError] = useState(null);
  const [universeNonce, setUniverseNonce] = useState(0);
  const [selected, setSelected] = useState(() => load('strategy.allocSelected', null));
  const [modes, setModes] = useState(() => load('strategy.allocModes', {}));
  // Every strategy's weightage is editable in its own popup; autos recompute
  // over the selection unless the user has overridden that strategy.
  const [overrides, setOverrides] = useState(() => load('strategy.allocOverrides', {}));
  const [editor, setEditor] = useState(null); // {label, draft} while popup open
  const [fromYear, setFromYear] = useState(() => load('strategy.allocFrom', 2017));
  const [toYear, setToYear] = useState(() => load('strategy.allocTo', 2026));
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [customPicked, setCustomPicked] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // ONE sizing rule — nothing to pick, nothing to persist.
  const sizingMethod = 'quality';
  const [cryptoRisk, setCryptoRisk] = useState(() => {
    const v = load('strategy.allocCryptoRisk.v3', 20);
    return [10, 15, 20, 25].includes(v) ? v : 20;
  });
  // ₹100 Cr - ₹1,000 Cr only for now (Apal 2026-10-28): a larger stored
  // amount comes back as ₹1,000 Cr, never a refused request
  const [fundCr, setFundCr] = useState(() => Math.min(load('strategy.allocFundCr', 100), MAX_AMOUNT_CR));
  // Display unit only — sizing math stays in INR crores end to end
  const [currency, setCurrency] = useState(() => load('strategy.allocCurrency', 'cr'));
  const [liqOpen, setLiqOpen] = useState(false);
  // Page-scoped cost realism flag — 'flat' is the vanilla model everywhere
  // else; 'size_aware' charges each strategy's own position sizes by the
  // sqrt-law (Apal 2026-09-24: show how massive positions reduce returns)
  const [impactModel, setImpactModel] = useState(() => load('strategy.allocImpactModel', 'flat'));
  useEffect(() => { localStorage.setItem('strategy.allocImpactModel', JSON.stringify(impactModel)); }, [impactModel]);
  useEffect(() => { localStorage.setItem('strategy.allocCurrency', JSON.stringify(currency)); }, [currency]);
  const [lastChecked, setLastChecked] = useState(null);
  const [netCosts, setNetCosts] = useState(() => load('strategy.allocNetCosts.v2', true));
  const [execution, setExecution] = useState(() => load('strategy.allocExecution.v2', load('reports.executionMode.v2', 'next_day_open')));
  // Horizon: rows become rolling any-entry N-year outcomes (client-side)
  const [horizon, setHorizon] = useState(() => {
    const v = load('strategy.allocHorizon', 1);
    return [1, 2, 3].includes(v) ? v : 1;
  });
  useEffect(() => { localStorage.setItem('strategy.allocHorizon', JSON.stringify(horizon)); }, [horizon]);

  // Fetch the universe once (and on retry). Server already applied every
  // permission rule — what arrives is exactly what this user may see.
  useEffect(() => {
    let dead = false;
    setUniverseError(null);
    apiService.getUniverse({ scope: 'allocation' }).then(res => {
      if (dead) return;
      const regions = res.data?.regions || [];
      setUniverse(regions); // module registry for helpers
      setUniverseState(regions);
      const names = regions.flatMap(r => r.symbols.map(s => s.name));
      setSelected(prev => Array.isArray(prev) ? prev.filter(n => names.includes(n)) : names);
      setModes(prev => {
        const next = { ...prev };
        regions.forEach(r => r.symbols.forEach(s => {
          if (!(s.name in next)) next[s.name] = s.shorts ? 'ls' : 'l';
        }));
        return next;
      });
    }).catch(() => { if (!dead) setUniverseError('Could not load your index universe.'); });
    return () => { dead = true; };
  }, [universeNonce]);

  const labelMap = UNIVERSE.labels;
  const allNames = UNIVERSE.names;
  const canShort = (sym) => UNIVERSE.shortable.has(sym); // server verdict: capability ∩ tradability ∩ grant

  const toggle = (name) => setSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);
  const toggleRegion = (code) => {
    const names = ((universe || []).find(r => r.code === code)?.symbols || []).map(s => s.name);
    setSelected(prev => names.every(n => prev.includes(n))
      ? prev.filter(n => !names.includes(n))
      : [...new Set([...prev, ...names])]);
  };

  const reqId = useRef(0);
  const inFlight = useRef(null);   // the running evaluation — a newer change cancels it
  const lastExec = useRef(null);
  const evaluate = useCallback(async (sel, m, ov, fy, ty, rule, fund, riskPct, net, exec_) => {
    if (!sel.length) return;
    const myReq = ++reqId.current;
    if (inFlight.current) inFlight.current.abort();
    const abort = new AbortController();
    inFlight.current = abort;
    setLoading(true);
    setError(null);
    try {
      const sets = effectiveSets(sel, ov).map(st =>
        st.label === 'data_driven' ? { ...st, method: rule || 'best' } : st);
      // Client-side clamp mirrors the server fence: 'ls' only where the
      // served universe grants it — the request never claims a book the
      // user can't see.
      const res = await apiService.evaluateAllocation({
        sets, modes: Object.fromEntries(sel.map(sym =>
          [sym, (m[sym] === 'ls' && UNIVERSE.shortable.has(sym)) ? 'ls' : 'l'])),
        from_year: fy, to_year: ty, fund_cr: fund || 100, crypto_risk_pct: riskPct || 20,
        impact_model: impactModel,
        net_costs: !!net, execution: exec_ || 'next_day_open',
        cost_config: net ? (JSON.parse(localStorage.getItem('reports.costConfig') || 'null') || {}) : {},
      }, { signal: abort.signal });
      if (myReq !== reqId.current) return; // superseded by a newer change
      setData(res.data);
      setLastChecked(new Date());
      lastExec.current = exec_ || 'next_day_open';
      localStorage.setItem('strategy.allocSelected', JSON.stringify(sel));
      localStorage.setItem('strategy.allocModes', JSON.stringify(m));
      localStorage.setItem('strategy.allocOverrides', JSON.stringify(ov));
      localStorage.setItem('strategy.allocFrom', JSON.stringify(fy));
      localStorage.setItem('strategy.allocTo', JSON.stringify(ty));
      localStorage.setItem('strategy.allocCryptoRisk.v3', JSON.stringify(riskPct || 20));
      localStorage.setItem('strategy.allocNetCosts.v2', JSON.stringify(!!net));
      localStorage.setItem('strategy.allocExecution.v2', JSON.stringify(exec_ || 'next_day_open'));
      localStorage.setItem('strategy.allocFundCr', JSON.stringify(fund || 100));
    } catch (e) {
      if (myReq === reqId.current && !isCanceled(e)) setError(e.response?.data?.error || 'Evaluation failed — try again.');
    } finally {
      if (myReq === reqId.current) setLoading(false);
    }
  }, [impactModel]);

  // No Evaluate button: every config change re-runs on its own. First mount
  // fires immediately; later changes debounce briefly so a burst of chip
  // clicks becomes one request. Results update in place (stale-while-revalidate).
  const firstRun = useRef(true);
  const lastReload = useRef(reloadKey);
  useEffect(() => {
    // No evaluation before the universe answers — `selected` isn't resolved
    // against the user's permitted names until then.
    if (!universe || !Array.isArray(selected) || !selected.length) return;
    const isReload = lastReload.current !== reloadKey;
    lastReload.current = reloadKey;
    if (firstRun.current || isReload) {
      // first paint and the Reload button both run NOW — the debounce is
      // only for bursts of chip clicks
      firstRun.current = false;
      evaluate(selected, modes, overrides, fromYear, toYear, sizingMethod, fundCr, cryptoRisk, netCosts, execution);
      return;
    }
    // the click shows at once: the strip and the dim start now, not after the
    // half-second burst window (a filter click must never look like nothing happened)
    setLoading(true);
    const t = setTimeout(() => evaluate(selected, modes, overrides, fromYear, toYear, sizingMethod, fundCr, cryptoRisk, netCosts, execution), 500);
    return () => clearTimeout(t);
  }, [reloadKey, evaluate, universe, selected, modes, overrides, fromYear, toYear, sizingMethod, fundCr, cryptoRisk, netCosts, execution, impactModel]);

  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(null);
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);

  // CAGR and Score sit level across the three books whatever their tops hold
  // (a wrapped badge, more stretches, more indexes): measure each column's
  // top block after layout and give all of them the tallest height
  const headTopRefs = useRef({});
  useLayoutEffect(() => {
    const level = () => {
      const els = Object.values(headTopRefs.current).filter(Boolean);
      if (els.length < 2) return;
      els.forEach(e => { e.style.minHeight = '0px'; });
      const h = Math.max(...els.map(e => e.getBoundingClientRect().height));
      if (h > 0) els.forEach(e => { e.style.minHeight = `${Math.ceil(h)}px`; });
    };
    level();
    window.addEventListener('resize', level);
    return () => window.removeEventListener('resize', level);
  });

  // ── All hooks are above this line ──
  if (universeError) {
    return (
      <div className="text-sm text-red-400 flex items-center gap-3" data-testid="universe-error">
        {universeError}
        <button onClick={() => setUniverseNonce(n => n + 1)}
          className="px-3 py-1 rounded-md bg-dark-700 hover:bg-dark-600 text-white text-xs">Retry</button>
      </div>
    );
  }
  if (!universe || !Array.isArray(selected)) {
    // the page's shape while the index universe loads: filters, chips, leaderboard
    return (
      <div data-testid="universe-skeleton" className="space-y-4">
        {/* the page's own filter line, the collapsed index row and the amount, then the leaderboard */}
        <div className="flex flex-wrap items-end gap-4">
          {[['Costs', 'w-28'], ['Liquidity impact', 'w-40'], ['Execution', 'w-56'], ['Horizon', 'w-32'], ['Period', 'w-64']].map(([label, w]) => (
            <div key={label}>
              <span className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold mb-1.5 block">{label}</span>
              <Skel className={`h-9 ${w}`} rounded="rounded-lg" />
            </div>
          ))}
        </div>
        <div>
          <div className="text-sm font-semibold text-white mb-2">› Select indexes</div>
          <div className="flex flex-wrap gap-1.5" data-testid="index-chips-pending">{Array.from({ length: 10 }, (_, i) => <Skel key={i} className="h-7 w-24" />)}</div>
        </div>
        <div className="flex items-center gap-3 pt-4 border-t border-dark-700/70">
          <span className="text-dark-400 text-[11px] uppercase tracking-widest font-semibold">Amount</span>
          <Skel className="h-9 w-24" rounded="rounded-lg" /><Skel className="h-9 w-28" rounded="rounded-lg" />
        </div>
        <ProcessingStatus active title="Loading allocation" />
        <AllocationTableSkeleton />
      </div>
    );
  }

  const years = data?.years || [];
  const holdYears = new Set(data?.hold_years || []);
  const results = [...(data?.results || [])].sort(
    (a, b) => STRATEGY_ORDER.indexOf(a.label) - STRATEGY_ORDER.indexOf(b.label));
  // WINNER respects feasibility: a book wearing the "exceeds executable
  // limits" badge can't take the crown — its printed CAGR isn't achievable
  // at this fund size. If EVERY strategy breaches, fall back to plain max.
  const breachedSet = new Set(results.filter(r => strategyBreaches(
    r.weights, data?.config?.instruments, fundCr
  ).length > 0).map(r => r.label));
  // house crypto policy: above-cap books are flagged and can't take the crown
  results.forEach(r => {
    if (cryptoCapExcess(r.weights, data?.config?.instruments, cryptoRisk)) breachedSet.add(r.label);
  });
  // era-renorm honesty: a strategy whose renormalized era book breaches the
  // concentration walls isn't comparable on this window (searched DD maps
  // run real capped era books server-side — exempt)
  const searchedDD = true;    // the one rule runs real era books
  results.forEach(r => {
    if (r.label === 'data_driven' && searchedDD) return;
    if (eraBreaches(r.weights, data?.config?.instruments, data?.inceptions,
                    data?.years?.[0], data?.years?.[data.years.length - 1]).length > 0) {
      breachedSet.add(r.label);
    }
  });
  // WINNER = best on the displayed number among books that can ACTUALLY be
  // executed. Apal 2026-09-25 saw Custom crowned at 14.9% while Data-driven
  // showed 28.5% — the cause was a real bug (our own book was wrongly
  // flagged at 21% crypto against a 20% cap), not this rule: naming the
  // best runnable book is the useful answer, and the flag on a better-
  // looking column says why it was passed over. The one change: if NOTHING
  // is executable, nobody is crowned — the old fallback crowned a breaching
  // book, which is the one outcome that cannot be defended.
  const eligible = results.filter(r => !breachedSet.has(r.label));
  const crowned = eligible.reduce(
    (a, r) => (a === null || r.window_cagr > a.window_cagr ? r : a), null);
  const winnerLabel = crowned ? crowned.label : null;
  // Every column reserves the same legend height so CAGR, Score and the
  // year rows start on the SAME line in all three (Apal 2026-09-25: "no
  // matter what portfolio keep in mind to have equal number of rows so all
  // 3 columns look easy to compare, give spacing it's ok"). The tallest
  // book sets the reservation; shorter ones pad with space.
  const legendRows = Math.max(1, ...results.map(r => {
    const held = Object.keys(r.weights || {}).filter(k => r.weights[k] > 0).length;
    const deployed = Object.values(r.weights || {}).reduce((a, v) => a + v, 0);
    return Math.ceil((held + (Math.round(100 - deployed) >= 1 ? 1 : 0)) / 2);
  }));
  const LEGEND_ROW_PX = 24;                      // one legend line incl. gap
  const legendMinPx = Math.max(136, legendRows * LEGEND_ROW_PX);

  const strategySets = effectiveSets(selected, overrides);
  const yearOptionsBase = (data && data.all_years && data.all_years.length) ? data.all_years : FALLBACK_YEARS;
  // a stored year outside the served range must stay selectable, never a blank select
  const yearOptions = [...new Set([...yearOptionsBase, fromYear, toYear])].sort();
  // Period — the shared All history · Year · Custom control. The mode is read
  // from the years themselves (one year → Year, the whole record → All
  // history), so a saved 2017–2026 window comes back as Custom, unchanged.
  const yrMin = yearOptionsBase[0], yrMax = yearOptionsBase[yearOptionsBase.length - 1];
  const periodMode = customPicked ? 'custom'
    : fromYear === toYear ? 'year'
    : (fromYear <= yrMin && toYear >= yrMax) ? 'all' : 'custom';
  const pickAll = () => { setCustomPicked(false); setFromYear(yrMin); setToYear(yrMax); };
  const pickYear = (y) => { setCustomPicked(false); setFromYear(y); setToYear(y); };
  const openEditor = (label) => {
    if (!EDITABLE_STRATEGIES.has(label)) return;
    const st = strategySets.find(x => x.label === label);
    const base = st ? st.weights : (autoWeights(label, selected) || {});
    setEditor({ label, draft: { ...base } });
  };
  const hasCustomRow = strategySets.some(st => st.label === 'custom');

  return (
    <div>
      {/* ── ONE line of filters at top, same order as the global bar on the
          other tabs: Costs · Liquidity · Execution · Horizon · Period ── */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5" data-testid="alloc-trading">
        <div className={FILTER_ROW}>
          <div data-testid="alloc-costs">
            <SegmentControl label="Costs" value={netCosts ? 'net' : 'gross'} onChange={(v) => setNetCosts(v === 'net')}
              options={[
                { key: 'gross', label: 'Gross', activeBg: 'bg-dark-500 text-white', title: 'Raw strategy returns, no costs' },
                { key: 'net', label: 'Net', activeBg: 'bg-amber-500 text-white', title: 'Apply impact + carry + interest costs to strategy returns' },
              ]} />
          </div>
          {netCosts && (
            <div data-testid="alloc-impact-model">
              <SegmentControl label="Liquidity impact" value={impactModel} onChange={setImpactModel}
                options={[
                  { key: 'flat', label: 'Ignored', activeBg: 'bg-dark-500 text-white', title: 'Vanilla costs — position size never moves the trading cost (no liquidity consideration)' },
                  { key: 'size_aware', label: 'Realistic', activeBg: 'bg-rose-600 text-white', title: 'Each strategy pays what its OWN position sizes cost by the square-root impact law at this amount — concentrated books at scale pay visibly more' },
                ]} />
            </div>
          )}
          <div data-testid="alloc-execution">
            <SegmentControl label="Execution" value={execution} onChange={setExecution}
              options={EXECUTION_OPTIONS} />
          </div>
          <div data-testid="alloc-horizon">
            <SegmentControl label="Horizon" value={horizon} onChange={setHorizon}
              options={HORIZON_OPTIONS.map(op => ({ key: op.key, label: op.label,
                title: op.key === 1 ? 'One row per calendar year'
                  : `Every possible ${op.key}-year entry point — each row shows what that hold delivered` }))} />
          </div>
          <PeriodControl id="alloc-period-label" testid="alloc-period" mode={periodMode}
            presets={[{ key: 'all', label: 'All history', title: `${yrMin} – ${yrMax}` }]} onPreset={pickAll}
            years={[...yearOptionsBase].sort((x, y) => y - x)} pickedYear={fromYear} onYear={pickYear}
            yearTestid="alloc-year" yearLabelTestid="alloc-year-label"
            onCustom={() => setCustomPicked(true)}
            customYears={{ from: fromYear, to: toYear, years: yearOptions,
              onFrom: v => setFromYear(parseInt(v, 10)), onTo: v => setToYear(parseInt(v, 10)),
              fromTestid: 'year-from', toTestid: 'year-to' }} />
        </div>
        <span className={`text-xs h-9 flex items-center ${loading && lastExec.current && execution !== lastExec.current ? 'text-amber-300 font-medium' : 'text-dark-400'}`}
          aria-live="polite" data-testid="alloc-updating">
          {loading
            ? (lastExec.current && execution !== lastExec.current
                ? `Rebuilding the full trade history for ${execution === 'next_day_open' ? 'Next open' : execution === 'next_day_close' ? 'Next close' : 'Close'} — first switch takes ~20s…`
                : data ? 'Updating…' : 'Building the return series (first run, ~20s)…')
            : lastChecked
              ? `✓ Checked ${lastChecked.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false })}`
              : ''}
        </span>
      </div>

      {/* ── Index selection — the same header, chips and box as Compare,
          Signal Analysis and Quarterly Wins. Collapsed, the chips still state
          every index's book. ── */}
      <div className="mb-4">
        <SelectorToggle open={selectorOpen} onToggle={() => setSelectorOpen(o => !o)} count={selected.length} />
        {selectorOpen ? (
          <div className="bg-dark-800/50 rounded-lg border border-dark-700 p-4">
            <IndexChipRows
              regions={universe}
              selected={selected}
              onToggle={toggle}
              onRegionToggle={toggleRegion}
              canShort={canShort}
              modeFor={sym => (modes[sym] === 'ls' ? 'ls' : 'l')}
              onModeToggle={sym => setModes(prev => ({ ...prev, [sym]: prev[sym] === 'ls' ? 'l' : 'ls' }))}
            />
            <div className="flex items-center gap-3 mt-3 pt-2 border-t border-dark-700">
              {selected.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-dark-400 hover:text-red-400">Clear all</button>}
              <button onClick={() => setSelected([...allNames])} className="text-xs text-dark-400 hover:text-teal-400">Select all</button>
              {selected.length === 0 && <span className="text-xs text-dark-500">Select indexes to build allocations</span>}
            </div>
          </div>
        ) : selected.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="alloc-selected-chips">
            {selected.map(sym => {
              // Every chip states its book: violet L+S, teal L (chosen),
              // muted L when the instrument can't short at all.
              const book = canShort(sym) ? (modes[sym] === 'ls' ? 'ls' : 'l') : 'na';
              return (
                <button key={sym} onClick={() => setSelectorOpen(true)}
                  className="pl-2.5 pr-1 py-1 rounded-md bg-dark-700/70 border border-dark-600/40 text-dark-200 text-xs font-medium hover:text-white hover:border-dark-500 transition-colors inline-flex items-center gap-1.5"
                  title={book === 'ls' ? 'Long + short book — click to change'
                    : book === 'l' ? 'Long-only (your choice) — click to change'
                    : 'Long-only — this instrument has no tradable shorts'}>
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: sleeveColor(sym) }} />
                  {labelMap[sym] || sym}
                  <span data-testid={`chip-book-${sym}`}
                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${
                      book === 'ls' ? 'bg-violet-600/80 text-white'
                      : book === 'l' ? 'bg-teal-700/80 text-teal-100'
                      : 'bg-dark-600 text-dark-400'}`}>
                    {book === 'ls' ? 'L+S' : 'L'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Amount — with the sizing it drives: capacity, cash and the
          liquidity caps all read from it (Apal 2026-10-15: one line of
          filters at top, "other relevant things in their sections") ── */}
      <div className="flex flex-wrap items-center gap-3 pt-4 mb-3 border-t border-dark-700/70" data-testid="alloc-filters">
        <span className={`${FILTER_LABEL} !mb-0`}>Amount</span>
        {(() => {
          // ONE control, not an input plus a chip row (Apal 2026-09-25:
          // "remove this if it's bothering spacing"). A dropdown can only
          // ever show the amount that is actually selected.
          const usdInr = (data && data.config && data.config.usd_inr) || 96;
          const toCr = (m) => Math.max(1, Math.round(m * usdInr / 10));
          const steps = currency === 'usd'
            ? [10, 50, 100].map(m => ({ cr: toCr(m), l: `$${m}M` }))
            : [100, 500, 1000].map(cr => ({ cr, l: cr >= 1000 ? `₹${cr / 1000}K Cr` : `₹${cr} Cr` }));
          // a stored amount outside the list stays selectable, never blank
          const opts = steps.some(o => o.cr === fundCr)
            ? steps
            : [...steps, { cr: fundCr, l: currency === 'usd' ? `$${Math.round(fundCr * 10 / usdInr)}M` : `₹${fundCr} Cr` }]
                .sort((a, b) => a.cr - b.cr);
          return (
            <div className="flex items-center gap-1.5">
              <div className="h-9 flex items-stretch gap-1 bg-dark-700 p-1 rounded-lg border border-dark-600/30" data-testid="alloc-currency">
                <button onClick={() => setCurrency('cr')} title="Indian rupees, crores"
                  className={`px-2.5 flex items-center rounded-md text-[13px] font-semibold transition-all ${currency === 'cr' ? 'bg-primary-600 text-white shadow-md' : 'text-dark-300 hover:text-white hover:bg-dark-600'}`}>₹Cr</button>
                <button onClick={() => setCurrency('usd')} title={`US dollars, millions (₹${usdInr}/$ display rate — sizing stays in ₹)`}
                  className={`px-2.5 flex items-center rounded-md text-[13px] font-semibold transition-all ${currency === 'usd' ? 'bg-primary-600 text-white shadow-md' : 'text-dark-300 hover:text-white hover:bg-dark-600'}`}>$M</button>
              </div>
              <FilterSelect value={fundCr} onChange={e => setFundCr(parseInt(e.target.value))}
                data-testid="fund-size" aria-label="Amount"
                title="Depth rule: each sleeve ≤ what its market absorbs in one fill">
                {opts.map(o => <option key={o.cr} value={o.cr}>{o.l}</option>)}
              </FilterSelect>
            </div>
          );
        })()}
      </div>

      {error && <div className="text-sm text-red-400 mb-4">{error}</div>}

      {/* ── The leaderboard IS the page ── first run: its shape under the strip */}
      {!data && !error && Array.isArray(selected) && selected.length > 0 && (
        <div data-testid="allocation-first-load">
          <ProcessingStatus active title="Loading allocation"
            what={[netCosts ? 'Net' : 'Gross', (EXECUTION_OPTIONS.find(o => o.key === execution) || {}).label].filter(Boolean).join(' · ')} />
          <AllocationTableSkeleton />
        </div>
      )}
      {data && (() => {
        // Horizon view: every row is a rolling N-year any-entry outcome,
        // compounded client-side from the same audited yearly numbers.
        const r1 = (v) => Math.round(v * 10) / 10;
        const windowsByLabel = {};
        const viewResults = horizon === 1 ? results : results.map(r => {
          const ws = rollingWindows(r.yearly, years, horizon);
          windowsByLabel[r.label] = ws;
          return { ...r,
            yearly: Object.fromEntries(ws.map(w => [w.endYear, r1(w.value)])),
            bh_yearly: r.bh_yearly
              ? Object.fromEntries(rollingWindows(r.bh_yearly, years, horizon).map(w => [w.endYear, r1(w.value)]))
              : r.bh_yearly };
        });
        const baseWs = horizon === 1 ? null : (windowsByLabel[results[0]?.label] || []);
        const vYears = horizon === 1 ? years : baseWs.map(w => w.endYear);
        const labelByEnd = horizon === 1 ? null : Object.fromEntries(baseWs.map(w => [w.endYear, w.label]));
        const vHold = horizon === 1 ? holdYears
          : new Set(baseWs.filter(w => holdYears.has(w.startYear)).map(w => w.endYear));
        const vByLabel = Object.fromEntries(viewResults.map(v => [v.label, v]));
        const yearMax = {};
        vYears.forEach(y => { yearMax[y] = Math.max(...viewResults.map(r => r.yearly[y] ?? -Infinity)); });
        const wonCount = r => vYears.filter(y => vByLabel[r.label].yearly[y] === yearMax[y]).length;
        return (
        <div>
        {(() => {
          const sz = data.sizing && data.sizing.data_driven;
          const dd = (data.results || []).find(r => r.label === 'data_driven');
          if (!sz || !dd) return null;
          const deployed = Object.values(dd.weights).reduce((a, v) => a + v, 0);
          const cash = Math.round(100 - deployed);
          const usdInr = (data.config && data.config.usd_inr) || 96;
          const limitCr = sz.full_deploy_limit_cr;
          const capsSum = sz.caps_sum_pct;
          const limitLine = capsSum != null && capsSum < 100
            ? `Concentration caps allow at most ${Math.round(capsSum)}% deployed at ANY amount for this selection — the rest is always cash at 6.5%.`
            : limitCr
              ? `Fully deployable up to ₹${limitCr.toLocaleString()} Cr (~$${Math.round(limitCr * 10 / usdInr).toLocaleString()}M); beyond that, market depth turns the excess into cash.`
              : null;
          const limitEl = limitLine
            ? <div data-testid="deploy-limit" className="text-xs text-dark-400 mb-3">Capacity: {limitLine}</div>
            : null;
          if (cash < 1) return limitEl;
          // Name the TRUE binder per sleeve — SEBI position ceiling vs market
          // depth — from the same served config the Liquidity table uses.
          // (The old copy blamed 'depth' even when the ceiling was the cause,
          // and pitched 'US indexes are coming' after they had arrived —
          // Apal 2026-09-24: this page does not get to be sloppy.)
          const instByAlloc = Object.fromEntries((data.config?.instruments || []).map(ins => [ins.symbol, ins]));
          const binding = Object.entries(sz.stats || {})
            .filter(([sym, st]) => st.cap_pct < 100 && (dd.weights[sym] || 0) >= st.cap_pct - 0.5)
            .map(([sym, st]) => {
              const ins = instByAlloc[sym] || {};
              const partPct = depthPct(ins, sz.fund_cr || 100);
              const posPct = ins.pos_limit_cr ? 100 * ins.pos_limit_cr / Math.max(sz.fund_cr || 100, 1e-9) : Infinity;
              const why = posPct < partPct
                ? `SEBI position ceiling ₹${ins.pos_limit_cr.toLocaleString()} Cr`
                : `₹${Math.round(ins.max_position_cr ?? st.max_position_cr ?? 0).toLocaleString()} Cr the market absorbs in one fill`;
              return `${NICE[sym] || sym} ${st.cap_pct}% (${why})`;
            });
          const dropped = Object.keys(sz.excluded || {}).map(sym => NICE[sym] || sym);
          const unselectedMkts = (universe || [])
            .filter(r => r.symbols.every(sy => !selected.includes(sy.name)))
            .map(r => r.region);
          return (
            <div className="bg-amber-500/[0.07] border border-amber-500/25 rounded-lg px-4 py-3 mb-3 text-sm text-amber-200/90 leading-relaxed" data-testid="capacity-note">
              <span className="font-semibold">At ₹{(sz.fund_cr || 100).toLocaleString()} Cr this selection can deploy only {Math.round(deployed)}% as modeled</span>
              {binding.length > 0 && <> — binding limits: {binding.join(', ')}</>}
              {dropped.length > 0 && <>; {dropped.join(', ')} excluded (see Why these weights?)</>}.
              The remaining {cash}% sits in treasuries at 6.5%.
              {unselectedMkts.length > 0
                ? <> Capacity rises only by adding unselected markets ({unselectedMkts.join(', ')}) — the selected ones are at their executable ceilings.</>
                : <> Every selectable market is already in — this is the executable ceiling of the whole universe at this amount.</>}
              {limitLine && <div className="mt-1.5 text-amber-200/70 text-xs">Capacity: {limitLine}</div>}
            </div>
          );
        })()}
        {/* Liquidity — the SERVED depth numbers and the one equation that
            turns them into caps. Nothing here is typed by hand: ADV and caps
            come from engine_config, the % is computed in front of the user. */}
        {data.config && Array.isArray(data.config.instruments) && selected.length > 0 && (() => {
          const usdInr = data.config.usd_inr || 96;
          const rows = data.config.instruments
            .filter(i => selected.includes(i.symbol))
            .sort((a, b) => (b.adv_cr || 0) - (a.adv_cr || 0));
          if (!rows.length) return null;
          return (
            <div className="mb-4">
              <button data-testid="liquidity-toggle" onClick={() => setLiqOpen(v => !v)}
                className="text-xs text-dark-400 hover:text-teal-300 transition-colors">
                {liqOpen ? '▾' : '▸'} Liquidity — how depth becomes a cap
              </button>
              {liqOpen && (
                <div data-testid="liquidity-table" className="mt-2 bg-dark-800/60 border border-dark-700 rounded-lg p-4 overflow-x-auto">
                  <div className="text-xs text-dark-400 mb-3">
                    max sleeve % = <span className="text-teal-300 font-medium">smaller of: quiet-day turnover × (impact charged ÷ daily volatility)², and in India {data.config.close_window_participation_pct ?? 10}% of the closing half hour ({data.config.close_window_share_pct ?? 8}% of the day) — ÷ amount</span>
                    <span className="text-dark-500"> — what the market absorbs in one fill; at ₹{fundCr.toLocaleString()} Cr, then clipped by the concentration cap</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead><tr className="text-dark-500 text-[11px] uppercase tracking-wider">
                      <th className="text-left py-1.5 pr-3 font-medium">Index</th>
                      <th className="text-right py-1.5 px-3 font-medium">Quiet-day turnover</th>
                      <th className="text-right py-1.5 px-3 font-medium">Daily volatility</th>
                      <th className="text-right py-1.5 px-3 font-medium">Impact charged</th>
                      <th className="text-right py-1.5 px-3 font-medium">Market absorbs</th>
                      <th className="text-right py-1.5 px-3 font-medium">Position ceiling</th>
                      <th className="text-right py-1.5 px-3 font-medium">Liquidity allows</th>
                      <th className="text-right py-1.5 px-3 font-medium">Concentration cap</th>
                      <th className="text-right py-1.5 px-3 font-medium">Effective max</th>
                      <th className="text-left py-1.5 pl-3 font-medium">Binds</th>
                    </tr></thead>
                    <tbody>
                      {rows.map(i => {
                        const liqPart = depthPct(i, fundCr);
                        const posPct = i.pos_limit_cr ? 100 * i.pos_limit_cr / Math.max(fundCr, 1e-9) : Infinity;
                        const liq = Math.min(100, liqPart, posPct);
                        const conc = i.concentration_cap_pct ?? 100;
                        const eff = Math.min(liq, conc);
                        const binds = conc <= liq ? 'caps' : (posPct < liqPart ? 'regulation' : 'depth');
                        const advTxt = currency === 'usd'
                          ? `$${Math.round((i.adv_cr || 0) * 10 / usdInr).toLocaleString()}M/day`
                          : `₹${(i.adv_cr || 0).toLocaleString()} Cr/day`;
                        const absorbTxt = i.max_position_cr == null ? 'no limit' : currency === 'usd'
                          ? `$${Math.round(i.max_position_cr * 10 / usdInr).toLocaleString()}M`
                          : `₹${i.max_position_cr < 10 ? i.max_position_cr.toFixed(1) : Math.round(i.max_position_cr).toLocaleString()} Cr`;
                        return (
                          <tr key={i.symbol} data-testid={`liq-row-${i.symbol}`} className="border-t border-dark-700/50">
                            <td className="py-2 pr-3">
                              <div className="text-white font-medium">{NICE[i.symbol] || i.symbol}</div>
                              {i.note && <div className="text-[10px] text-dark-500 leading-snug max-w-[340px] mt-0.5">{i.note}</div>}
                            </td>
                            <td className="py-2 px-3 text-right text-dark-200 tabular-nums">{advTxt}</td>
                            <td className="py-2 px-3 text-right text-dark-300 tabular-nums">{i.daily_vol_pct != null ? `${i.daily_vol_pct.toFixed(2)}%` : '—'}</td>
                            <td className="py-2 px-3 text-right text-dark-300 tabular-nums">{i.impact_side_pct != null ? `${i.impact_side_pct.toFixed(3)}%` : '—'}</td>
                            <td data-testid={`liq-absorbs-${i.symbol}`} className="py-2 px-3 text-right text-dark-200 font-medium tabular-nums">{absorbTxt}</td>
                            <td className="py-2 px-3 text-right text-dark-300 tabular-nums">{i.pos_limit_cr ? (currency === 'usd' ? `$${Math.round(i.pos_limit_cr * 10 / usdInr).toLocaleString()}M` : `₹${i.pos_limit_cr.toLocaleString()} Cr`) : '—'}</td>
                            <td className={`py-2 px-3 text-right tabular-nums ${liq < conc ? 'text-amber-300 font-semibold' : 'text-dark-300'}`}>{liq >= 100 ? '100%+' : `${liq.toFixed(1)}%`}</td>
                            <td className="py-2 px-3 text-right text-dark-300 tabular-nums">{conc}%</td>
                            <td className="py-2 px-3 text-right text-teal-300 font-semibold tabular-nums">{eff.toFixed(1)}%</td>
                            <td className={`py-2 pl-3 text-xs ${binds === 'caps' ? 'text-dark-500' : binds === 'regulation' ? 'text-red-400' : 'text-amber-400'}`}>{binds === 'caps' ? 'concentration' : binds === 'regulation' ? 'regulation (SEBI OI)' : 'market depth'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="mt-3 pt-3 border-t border-dark-700/60 text-xs text-dark-400">
                    Margin reality: SPAN + exposure ≈ <span className="text-dark-200 font-medium">{(data.config.span_exposure_pct ?? 12)}% of notional</span> — deploying
                    ₹{fundCr.toLocaleString()} Cr fully needs ~<span className="text-dark-200 font-medium">₹{Math.round(fundCr * (data.config.span_exposure_pct ?? 12) / 100).toLocaleString()} Cr</span> in
                    cash collateral with the clearing member, monitored second-by-second under peak-margin rules.
                  </div>
                  <div data-testid="execution-limits" className="mt-3 bg-red-500/[0.06] border border-red-500/25 rounded-lg px-4 py-3 text-xs text-dark-300 leading-relaxed">
                    <div className="text-red-300/90 font-semibold mb-1.5">Execution limits — what this model can and cannot promise</div>
                    <div>· Each position fills at one price (the close, or the open, per Execution), as the returns assume. A sleeve is capped where its own price impact would pass the impact cost already charged — measured on a quiet day, with the higher volatility, at the top of the published impact range — and in India at {data.config.close_window_participation_pct ?? 10}% of what trades in the closing half hour that sets NSE's close.</div>
                    <div>· Position ceilings above are the stricter SEBI reading (~5% of near-month open interest, client-group level): the broker's risk system rejects orders past them — they are walls, not guidance.</div>
                    <div>· Beyond a ceiling, deployment moves to basket trades, ETF creation or GIFT Nifty over multi-week windows — <span className="text-red-300/90">a different regime: above the capacity limit shown, these returns are NOT achievable as modeled.</span></div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {data.clamped && (
          <p className="text-sm text-amber-300/90 mb-2">
            Window starts {data.from_year_used} — no selected index has full-year data before that.
          </p>
        )}
        <ProcessingStatus active={loading} what={[netCosts ? 'Net' : 'Gross', (EXECUTION_OPTIONS.find(o => o.key === execution) || {}).label].filter(Boolean).join(' · ')} />
        <div className={`bg-dark-800 border border-dark-700 rounded-lg overflow-x-auto ${dimClass(loading)}`}>
          <table className="w-full table-fixed text-sm tabular-nums" data-testid="allocation-results">
            <colgroup>
              <col className={horizon === 1 ? 'w-14' : 'w-24'} />
              {results.map(r => <col key={r.label} />)}
              {!hasCustomRow && <col className="w-40" />}
            </colgroup>
            <thead>
              {/* Strategy identity cards as column headers; years run down */}
              <tr className="border-b border-dark-700">
                <th className={`${horizon === 1 ? 'w-20' : 'w-24'} px-3 sticky left-0 bg-dark-800 z-20`}></th>
                {results.map((r) => {
                  const isWinner = r.label === winnerLabel;
                  return (
                  <th key={r.label} className={`text-left align-top py-3.5 px-4 font-normal bg-dark-800 border-l border-dark-700 ${isWinner ? 'shadow-[inset_0_-2px_0_rgba(52,211,153,0.4)]' : ''}`}>
                    <div ref={el => { headTopRefs.current[r.label] = el; }} data-testid={`head-top-${r.label}`}>
                    {/* fixed title-row height: the Edit button is a touch
                        taller than the WINNER badge, which pushed Custom's
                        whole column 2px out of line with the others */}
                    <div className="flex items-center flex-wrap gap-y-1 min-h-[34px]">
                      <span className="text-white font-semibold text-xl">{SCENARIO_LABELS[r.label] || r.label}</span>
                      {isWinner && (
                        <span className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-400/40">WINNER</span>
                      )}
                      {(() => {
                        if (r.label === 'data_driven') return null;
                        const eb = eraBreaches(r.weights, data?.config?.instruments, data?.inceptions,
                          data?.years?.[0], data?.years?.[data.years.length - 1]);
                        return eb.length > 0 && (
                          <span data-testid={`era-breach-${r.label}`}
                            title={['Before every index in this mix was live, it renormalizes into a book that breaches the concentration walls the searched era books honor (they hold cash instead):',
                              ...eb,
                              'Its early-era numbers aren\'t comparable — not crown-eligible on this window.'].join('\n')}
                            className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-400/40 cursor-help">
                            era book over caps
                          </span>
                        );
                      })()}
                      {(() => {
                        const ex = cryptoCapExcess(r.weights, data?.config?.instruments, cryptoRisk);
                        return ex && (
                          <span data-testid={`crypto-cap-chip-${r.label}`}
                            title={`House risk policy caps BTC+ETH at ${ex.cap}% of the book — this mix holds ${ex.pct}%. Above-cap books aren't eligible for the crown.`}
                            className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-300 border border-amber-400/40 cursor-help">
                            above crypto cap
                          </span>
                        );
                      })()}
                      {(() => {
                        const br = strategyBreaches(r.weights, data?.config?.instruments, fundCr);
                        return br.length > 0 && (
                          <span data-testid={`strategy-breach-${r.label}`} title={br.join('\n')}
                            className="ml-2 px-1.5 py-0.5 rounded-full text-xs font-bold bg-red-500/15 text-red-300 border border-red-400/40 cursor-help">
                            exceeds executable limits
                          </span>
                        );
                      })()}
                      {EDITABLE_STRATEGIES.has(r.label) && overrides[r.label] && (
                        <span className="ml-1.5 text-xs px-2 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30">edited</span>
                      )}
                      {EDITABLE_STRATEGIES.has(r.label) && (
                        <button onClick={() => openEditor(r.label)}
                          className="ml-2 text-sm px-3 py-1 rounded border border-dark-600 text-dark-300 hover:text-white hover:border-dark-400 transition-colors">
                          Edit
                        </button>
                      )}
                    </div>
                    {(() => {
                      // the donut is the book the window ENDS on (today's, unless the
                      // window stops before an index joined); earlier books show in
                      // the year rows where they change (Apal 2026-10-29: "older way
                      // of showing allocation was cleaner")
                      const stretches = bookStretches(r.weights, data.results.indexOf(r), data.eras, years);
                      const last = stretches[stretches.length - 1];
                      return <BookDonut weights={(last && last.mix) || r.weights} minHeight={legendMinPx} />;
                    })()}
                    </div>
                    {/* Two stat blocks, label under the number — fixed height in
                        every column, so Score and the year rows stay level
                        across all three books at any width */}
                    <div className="flex items-end gap-6 mt-2.5">
                      <div className="leading-none">
                        <div className={`text-3xl font-bold tabular-nums ${isWinner ? 'text-emerald-400' : 'text-dark-100'}`}>{r.window_cagr}%</div>
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-dark-500 whitespace-nowrap">CAGR {years[0]}–{String(years[years.length - 1] || '').slice(2)}</div>
                      </div>
                      <div className="leading-none">
                        <div className="text-2xl font-bold text-dark-100 tabular-nums">{wonCount(r)} / {vYears.length}</div>
                        <div className="mt-1 text-[10px] uppercase tracking-wider text-dark-500 whitespace-nowrap">{horizon === 1 ? 'years won' : 'periods won'}</div>
                      </div>
                    </div>
                    {r.score && r.score.total != null && (
                      <div className="mt-2 relative" data-testid={`score-${r.label}`}>
                        <button onClick={() => setScoreOpen(scoreOpen === r.label ? null : r.label)}
                          data-testid={`score-open-${r.label}`}
                          className="w-full flex items-baseline gap-2 rounded bg-dark-700/40 border border-dark-700 hover:border-dark-500 px-2.5 py-1.5 text-left transition-colors">
                          <span className="text-[9px] uppercase tracking-wider text-dark-500">Score</span>
                          <span className={`text-xl font-bold tabular-nums ${scoreTone(r.score.total)}`}>{r.score.total}</span>
                          <span className="text-[10px] text-dark-500">/100</span>
                          <span className="ml-auto text-dark-500 text-xs">ⓘ</span>
                        </button>
                        {scoreOpen === r.label && (
                          <>
                            <div className="fixed inset-0 z-[60]" onClick={() => setScoreOpen(null)} />
                            <div className="absolute left-0 right-0 top-full z-[61] mt-1 bg-dark-700 border border-dark-500 rounded-lg p-3 shadow-2xl"
                              data-testid={`score-detail-${r.label}`}>
                              <div className="text-[11px] text-dark-300 leading-snug mb-2">
                                How soundly the book is built — not what it returned.
                              </div>
                              <div className="space-y-1.5">
                                {r.score.parts.map(p => (
                                  <div key={p.key} className="grid grid-cols-[112px_1fr_28px] items-center gap-2"
                                    data-testid={`score-part-${r.label}-${p.key}`}
                                    title={`${p.value} against a bar of ${p.bar}`}>
                                    <span className={`text-[11px] ${p.clears ? 'text-dark-300' : 'text-amber-300'}`}>{p.label}</span>
                                    <span className="h-1.5 rounded-full bg-dark-800 relative">
                                      <span className={`absolute left-0 top-0 h-1.5 rounded-full ${p.points >= 70 ? 'bg-emerald-400' : p.points >= 45 ? 'bg-amber-400' : 'bg-red-400'}`}
                                        style={{ width: `${Math.max(2, p.points)}%` }} />
                                    </span>
                                    <span className="text-[11px] text-dark-100 tabular-nums text-right">{p.points}</span>
                                  </div>
                                ))}
                              </div>
                              {r.score.also && (
                                <div className="mt-2.5 pt-2 border-t border-dark-600">
                                  <div className="text-[10px] uppercase tracking-wider text-dark-500 mb-1">Also measured — not scored</div>
                                  <div className="grid grid-cols-3 gap-y-1 text-[11px] text-dark-400" data-testid={`score-also-${r.label}`}>
                                    <span>Win rate <span className="text-dark-100">{r.score.also.win_rate}%</span></span>
                                    <span>Gain/loss <span className="text-dark-100">{r.score.also.gain_loss}x</span></span>
                                    <span>Avg trade <span className="text-dark-100">{r.score.also.avg_return}%</span></span>
                                    <span>Losing streak <span className="text-dark-100">{r.score.also.worst_streak}</span></span>
                                    <span>Beats index <span className="text-dark-100">{r.score.also.beats_index}x</span></span>
                                    <span>Headroom <span className="text-dark-100">{r.score.also.headroom}%</span></span>
                                  </div>
                                  <div className="mt-1.5 text-[10px] text-dark-500 leading-snug">
                                    These move together with the four above — showing them again would
                                    count the same thing twice.
                                  </div>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {horizon > 1 && (() => {
                      const st = windowStats(windowsByLabel[r.label] || []);
                      if (!st) return null;
                      return (
                        <div className="mt-1.5 text-sm text-dark-400" data-testid={`horizon-stats-${r.label}`}
                          title={`Across every possible ${horizon}-year entry point in this window (${st.count} of them): the worst outcome and the typical (median) outcome`}>
                          Worst {horizon}-yr entry <span className={`font-bold ${st.worst >= 0 ? 'text-emerald-300' : 'text-red-400'}`}>{st.worst > 0 ? '+' : ''}{st.worst.toFixed(1)}%</span>
                          <span className="text-dark-500"> · median {st.median > 0 ? '+' : ''}{st.median.toFixed(1)}%</span>
                        </div>
                      );
                    })()}
                    {r.label === 'data_driven' && (
                      <div className="flex items-center flex-wrap gap-2 mt-2.5">
                        {/* ONE rule now (Apal 2026-09-25: "Both dropdowns you
                            have if they suddenly remove niftybank from one vs
                            another is extremely confusing"). The method picker
                            is gone — there is nothing left to choose. */}
                        <label className="flex items-center gap-1.5 text-sm text-dark-400">
                          crypto cap
                          <FilterSelect compact value={cryptoRisk} onChange={e => setCryptoRisk(parseInt(e.target.value))}
                            data-testid="crypto-risk"
                            title="BTC + ETH together never exceed this share of the book. Crypto leads on return and win rate but is hyper-volatile, so it is limited here rather than penalised in the ranking.">
                            {[10, 15, 20, 25].map(v => <option key={v} value={v}>{v}%</option>)}
                          </FilterSelect>
                        </label>
                        <button onClick={() => setReasonsOpen(true)}
                          data-testid="why-weights"
                          className="text-sm px-3 py-1 rounded border border-dark-600 text-dark-300 hover:text-white hover:border-dark-400 transition-colors">
                          Why these weights?
                        </button>
                      </div>
                    )}

                  </th>
                  );
                })}
                {!hasCustomRow && (
                  <th className="text-left align-top py-3.5 px-4 font-normal bg-dark-800 border-l border-dark-700">
                    <button onClick={() => openEditor('custom')}
                      className="text-base text-teal-400 hover:text-teal-300 whitespace-nowrap">+ Add your own mix</button>
                  </th>
                )}
              </tr>
              <tr className="text-xs uppercase tracking-wider text-dark-500 border-t border-dark-700">
                <th className="px-3 pt-3 pb-2 sticky left-0 bg-dark-800 z-20"></th>
                {results.map((r) => (
                  <th key={r.label} className={`text-left px-4 pt-3 pb-2 font-semibold border-l border-dark-700 ${r.label === winnerLabel ? 'bg-emerald-500/[0.06]' : ''}`}>
                    <span className="inline-block w-28">Strategy</span>
                    <span className="text-dark-600">Buy &amp; hold</span>
                  </th>
                ))}
                {!hasCustomRow && <th className="border-l border-dark-700"></th>}
              </tr>
            </thead>
            <tbody>
              {[...vYears].reverse().map((y, di, arr) => {
                const prevShown = di > 0 ? arr[di - 1] : null;
                const era = horizon !== 1 ? null : (data.eras || []).find(e =>
                  prevShown !== null ? (e.year > y && e.year <= prevShown) : false);
                return (
                <React.Fragment key={y}>
                {era && (
                  <>
                  <tr data-testid={`era-divider-${era.year}`}>
                    <td className="sticky left-0 z-10 bg-dark-800 border-y-2 border-teal-500/40 px-2 py-2.5 text-center">
                      <span className="inline-block px-2 py-0.5 rounded-md bg-teal-500/20 border border-teal-400/40 text-teal-200 text-sm font-bold tabular-nums">{era.year}</span>
                    </td>
                    <td colSpan={results.length + (!hasCustomRow ? 1 : 0)}
                      className="py-2.5 px-4 border-y-2 border-teal-500/40 bg-teal-500/[0.09]">
                      <span className="text-base font-semibold text-teal-200"
                        title="Before an index has price history, the strategy simply runs without it: its percentage is divided among the indexes that existed, in proportion. From the year its data starts, the full mix takes over. Nothing is backfilled or guessed.">
                        {era.joined.map(sym => NICE[sym] || sym).join(' & ')} data starts here
                      </span>
                      <span className="text-sm text-dark-300 ml-3">— the years below ran without {era.joined.length === 1 ? 'it' : 'them'}, on this mix:</span>
                    </td>
                  </tr>
                  <tr>
                    <td className="sticky left-0 bg-dark-800 z-10 border-b border-teal-500/20"></td>
                    {results.map((r) => {
                      const mix = (era.mixes || [])[data.results.indexOf(r)];
                      return (
                        <td key={r.label} className="px-4 pb-3 pt-2 border-l border-dark-700/60 border-b border-teal-500/20 bg-teal-500/[0.04] align-top">
                          {mix ? <BookDonut weights={mix} testId={`era-book-${r.label}-${era.year}`} /> : '—'}
                        </td>
                      );
                    })}
                    {!hasCustomRow && <td className="bg-teal-500/[0.04] border-b border-teal-500/20"></td>}
                  </tr>
                  </>
                )}
                <tr className="border-t border-dark-700/60">
                  <td className={`py-2.5 px-3 text-left text-base font-bold sticky left-0 bg-dark-800 z-10 whitespace-nowrap ${vHold.has(y) ? 'text-teal-500' : 'text-dark-500'}`} style={{ minWidth: horizon === 1 ? 64 : 100 }}>{labelByEnd ? labelByEnd[y] : y}</td>
                  {results.map((rr) => {
                    const r = vByLabel[rr.label];
                    const win = r.yearly[y] === yearMax[y];
                    // Wash any REAL value at or under the threshold. Era-
                    // stitched years count — the then-live book genuinely ran
                    // (Apal: gating on full-universe inception hid every
                    // pre-crypto year and only Custom highlighted). The only
                    // exemption is a missing value: no data, no verdict.
                    const live = bookWasLive(r.weights, data.inceptions, y);
                    const band = yearBand(r.yearly[y], live);
                    const period = horizon === 1 ? 'year' : 'entry';
                    return (
                      <td key={r.label} data-testid={band ? `${band}-year-${r.label}-${y}` : undefined}
                        className={`py-2.5 px-4 text-left text-lg border-l border-dark-700/60 ${
                          band === 'danger' ? 'bg-red-500/[0.11]'
                            : band === 'warning' ? 'bg-amber-500/[0.09]'
                            : r.label === winnerLabel ? 'bg-emerald-500/[0.08]' : ''}`}
                        title={band === 'danger'
                          ? `${r.yearly[y]}% — a losing ${period}`
                          : band === 'warning'
                            ? `${r.yearly[y]}% — under ${SATISFACTORY_PCT}%, a thin ${period}`
                            : !live ? `No index in this mix was trading yet in ${y}` : undefined}>
                        <span className={`inline-block w-28 ${
                          band === 'danger' ? 'text-red-300 font-semibold'
                            : band === 'warning' ? 'text-amber-300 font-semibold'
                            : !live ? 'text-dark-600'
                            : win ? 'text-emerald-300 font-bold' : 'text-dark-300'}`}>
                          {r.yearly[y] > 0 ? '+' : ''}{r.yearly[y]}%
                        </span>
                        {r.bh_yearly && r.bh_yearly[y] != null && (
                          <span className={`text-base ${r.bh_yearly[y] < 0 ? 'text-red-400/70' : 'text-dark-500'}`}
                            title="Same allocation, buy & hold (no strategy)">
                            {r.bh_yearly[y] > 0 ? '+' : ''}{r.bh_yearly[y]}%
                          </span>
                        )}
                      </td>
                    );
                  })}
                  {!hasCustomRow && <td className="border-l border-dark-700/60"></td>}
                </tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
        );
      })()}
      <p className="text-sm text-dark-400 mt-4">
        Green = that {horizon === 1 ? "year's" : 'period’s'} winner · teal = fully hold-out.
        {data && data.config && (
          <>
            {' '}<button onClick={() => setAssumptionsOpen(true)} className="text-teal-500 hover:text-teal-400 underline decoration-dotted">
              Assumptions &amp; data
            </button>
          </>
        )}
      </p>

      {assumptionsOpen && data && data.config && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => setAssumptionsOpen(false)}>
          <div className="bg-dark-800 border border-dark-600 rounded-xl p-5 w-full max-w-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-testid="assumptions-popup">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-white">Every input the math uses — served live from the engine</span>
              <button onClick={() => setAssumptionsOpen(false)} className="text-dark-400 hover:text-white text-sm px-1">✕</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs mb-4">
              <div><span className="text-dark-500">Depth rule</span> <span className="text-white font-semibold">one fill ≤ impact charged; India ≤ {data.config.close_window_participation_pct ?? 10}% of the closing half hour</span></div>
              <div><span className="text-dark-500">Redundancy</span> <span className="text-white font-semibold">&gt;{data.config.redundancy_corr} corr folds into deeper</span></div>
              <div><span className="text-dark-500">Minimum position</span> <span className="text-white font-semibold">{data.config.min_alloc_pct}% (grid step)</span></div>
              <div><span className="text-dark-500">Idle cash</span> <span className="text-white font-semibold">{data.config.rf_annual_pct}%/yr</span></div>
              <div><span className="text-dark-500">Train ends</span> <span className="text-white font-semibold">{data.config.train_end}</span></div>
              <div><span className="text-dark-500">Risk window</span> <span className="text-white font-semibold">trailing {data.config.risk_estimation_window_years} full years</span></div>
              <div className="col-span-2 sm:col-span-3"><span className="text-dark-500">Early years</span> <span className="text-white font-semibold">before an index has data, its share is spread across the others — no backfilling, no guessing; the full mix starts when its history does</span></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs tabular-nums">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-dark-500 text-right">
                    <th className="text-left py-1.5">Instrument</th><th className="px-2">Conc. cap</th>
                    <th className="px-2">Quiet-day ₹Cr/day</th><th className="px-2">Absorbs ₹Cr</th><th className="px-2">Shorts</th><th className="text-left px-2">Source note</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.config.instruments || []).map(ins => (
                    <tr key={ins.symbol} className="border-t border-dark-700/60 text-right">
                      <td className="py-1.5 text-left text-dark-200">{NICE[ins.symbol] || ins.symbol}</td>
                      <td className="px-2 text-dark-300">{ins.concentration_cap_pct}%</td>
                      <td className="px-2 text-dark-300">{(ins.adv_cr || 0).toLocaleString()}</td>
                      <td className="px-2 text-dark-300">{ins.max_position_cr == null ? 'no limit' : ins.max_position_cr < 10 ? ins.max_position_cr.toFixed(1) : Math.round(ins.max_position_cr).toLocaleString()}</td>
                      <td className="px-2 text-dark-300">{ins.shorts ? 'yes' : 'no'}</td>
                      <td className="px-2 text-left text-dark-500 max-w-[280px]">{ins.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Data-driven reasoning popup — read-only */}
      {reasonsOpen && data && (() => {
        const dd = results.find(r => r.label === 'data_driven');
        if (!dd) return null;
        const sizing = null;   // both remaining methods are searched
        return (
          <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => setReasonsOpen(false)}>
            <div className="bg-dark-800 border border-dark-600 rounded-xl p-5 w-full max-w-2xl shadow-2xl" onClick={e => e.stopPropagation()} data-testid="weights-reasoning">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-white">Why these weights</span>
                <button onClick={() => setReasonsOpen(false)} className="text-dark-400 hover:text-white text-sm px-1">✕</button>
              </div>
              <p className="text-sm text-dark-400 mb-3">
                Each index earns its share from its own record since it started trading —
                return after costs, scaled by how often it wins. Nothing else sets the weight.
                An index never holds less than one it beats on both, unless a limit below stops it.
              </p>
              <div className="max-h-[60vh] overflow-y-auto pr-1">
                <table className="w-full text-[11px]" data-testid="weights-evidence">
                  <thead>
                    <tr className="text-dark-500 uppercase tracking-wider text-[9px]">
                      <th className="text-left font-semibold pb-1.5">Index</th>
                      <th className="text-right font-semibold pb-1.5">Weight</th>
                      <th className="text-right font-semibold pb-1.5">Return/yr</th>
                      <th className="text-right font-semibold pb-1.5">Win rate</th>
                      <th className="text-right font-semibold pb-1.5">In market</th>
                      <th className="text-right font-semibold pb-1.5">Since</th>
                      <th className="text-right font-semibold pb-1.5">Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(dd.weights).sort((a, b) => b[1] - a[1]).map(([sym, v]) => {
                      const s = data.sizing?.data_driven?.stats?.[sym] || {};
                      const pinned = s.cap_pct != null && v >= s.cap_pct - 0.01;
                      return (
                        <tr key={sym} className="border-t border-dark-700/60" data-testid={`evidence-${sym}`}>
                          <td className="py-1.5 text-dark-200">{NICE[sym] || sym}</td>
                          <td className="py-1.5 text-right text-white font-semibold tabular-nums">{v}%</td>
                          <td className="py-1.5 text-right text-dark-100 tabular-nums">{s.cagr != null ? `${s.cagr}%` : '—'}</td>
                          <td className="py-1.5 text-right text-dark-100 tabular-nums">{s.win_rate != null ? `${s.win_rate}%` : '—'}</td>
                          <td className="py-1.5 text-right text-dark-400 tabular-nums">{s.in_market != null ? `${s.in_market}%` : '—'}</td>
                          <td className="py-1.5 text-right text-dark-500 tabular-nums">{s.from_year || '—'}</td>
                          <td className={`py-1.5 text-right tabular-nums ${pinned ? 'text-amber-300' : 'text-dark-500'}`}
                            title={pinned ? `Held back by its executable limit — ${s.liq_note || 'market depth'}` : ''}>
                            {s.cap_pct != null ? `${s.cap_pct}%` : '—'}{pinned ? ' ⟵' : ''}
                          </td>
                        </tr>
                      );
                    })}
                    {Object.entries(data.sizing?.data_driven?.excluded || {}).map(([sym, why]) => (
                      <tr key={sym} className="border-t border-dark-700/60" data-testid={`skipped-${sym}`}>
                        <td className="py-1.5 text-dark-400">{NICE[sym] || sym}</td>
                        <td className="py-1.5 text-right text-dark-500">0%</td>
                        <td className="py-1.5 text-dark-400 text-[10px] leading-snug" colSpan={5}>{why}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-dark-500 mt-3">
                Return is net of trading costs and financing, measured from each index&apos;s own
                first full year — so an index is never judged on years before it existed. Win rate
                is the share of completed trades in profit. Time in market is shown for context and
                never weighted: its financing cost is already inside the return. A weight marked ⟵
                is capped by market depth, a SEBI position ceiling, or a concentration limit.
              </p>
            </div>
          </div>
        );
      })()}


      {/* Weightage editor popup */}
      {editor && (() => {
        const draftTotal = selected.reduce((a, sym) => a + (editor.draft[sym] || 0), 0);
        return (
          <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => setEditor(null)}>
            <div className="bg-dark-800 border border-dark-600 rounded-xl p-5 w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()} data-testid="weightage-editor">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-semibold text-white">Weightage — {SCENARIO_LABELS[editor.label]}</span>
                <span className={`text-sm font-bold tabular-nums ${draftTotal === 100 ? 'text-emerald-400' : 'text-red-400'}`}>Total {draftTotal}%</span>
              </div>
              <div className="space-y-2.5 max-h-[50vh] overflow-y-auto pr-1">
                {selected.map(sym => (
                  <div key={sym}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs text-dark-300">{NICE[sym] || sym}</span>
                      <span className="text-xs text-white font-semibold tabular-nums">{editor.draft[sym] || 0}%</span>
                    </div>
                    <input type="range" min={0} max={100} step={5} value={editor.draft[sym] || 0}
                      draggable={false} onDragStart={e => e.preventDefault()}
                      onChange={e => setEditor(prev => ({ ...prev, draft: { ...prev.draft, [sym]: parseInt(e.target.value) } }))}
                      className="w-full h-1.5 bg-dark-600 rounded-full appearance-none cursor-pointer accent-teal-500" />
                  </div>
                ))}
              </div>
              {draftTotal !== 100 && <p className="text-[11px] text-red-400 mt-2">weights must sum to 100% to apply</p>}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-dark-700">
                <button onClick={() => {
                  setOverrides(prev => { const n = { ...prev }; delete n[editor.label]; return n; });
                  setEditor(null);
                }} className="text-xs text-dark-400 hover:text-white underline decoration-dotted">Reset to default</button>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditor(null)}
                    className="px-3 py-1.5 rounded-lg text-xs text-dark-300 border border-dark-600 hover:text-white">Cancel</button>
                  <button disabled={draftTotal !== 100}
                    onClick={() => {
                      setOverrides(prev => ({ ...prev, [editor.label]: { ...editor.draft } }));
                      setEditor(null);
                    }}
                    className="px-4 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white disabled:opacity-40 hover:bg-primary-500">Apply</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
